/**
 * The PURE view behind `/settings/workspace/devices` (lane APPROVESURFACE) —
 * the owner's approval surface for headless device enrollment.
 *
 * WHY THIS IS A SEPARATE FILE AND NOT JUST THE PAGE. Two reasons, and the
 * second is the load-bearing one:
 *
 *  1. It is the ordinary container/presenter split: the route file
 *     (`./settings-device-enrollment.tsx`) owns `useApi`/`apiCall`, this file
 *     owns what the human sees. Neither forks a `@excellent/ui` atom.
 *
 *  2. IT MAKES THE PAGE PROVABLE. A component whose data arrives through a
 *     hook cannot be rendered outside a browser, so "the page shows the
 *     request it was handed" would be an unfalsifiable claim — the kind of
 *     assertion that passes identically when the page is broken. Split out,
 *     `pnpm test-verification-device-enrollment-approval-surface` renders this
 *     with TWO requests treated DIFFERENTLY (one pending, one already
 *     approved) and watches the assertion REFUSE when the view is neutered to
 *     render from a constant. The route file deliberately imports nothing from
 *     `./__root`, which drags the whole client shell (and a `.css` import) into
 *     any module that touches it.
 *
 * WHAT THIS FILE MAY NOT BECOME. It renders and it calls back. It must never
 * decide whether an approval is allowed, narrow a scope on its own authority,
 * or hold a credential. Every decision is made server-side by
 * `decideDeviceEnrollmentApproval`; see `excellent/server/routes/device-enrollment.ts`.
 */

import * as React from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CheckboxField,
  DataTable,
  IntentBadge,
  SettingsPageShell,
  SettingsSection,
  formatDateTime,
  formatRelativeTime,
  type DataTableColumn,
} from "@excellent/ui";
import type { DeviceEnrollmentRequestView } from "@excellent/ui/api/contracts/device-enrollment";

const STATE_INTENT = {
  pending: "warning",
  approved: "success",
  redeemed: "info",
  expired: "neutral",
} as const;

const STATE_LABEL = {
  pending: "Waiting on you",
  approved: "Approved",
  redeemed: "In use",
  expired: "Expired",
} as const;

export interface DeviceApprovalsViewProps {
  workspaceId: number | null;
  enrollments: DeviceEnrollmentRequestView[];
  isLoading: boolean;
  /** A refusal identity from the route, shown verbatim: an operator matching it
   * against the refusal table in the operator guide must see the SAME string
   * the CLI would have printed. */
  error: string | null;
  notice: string | null;
  busyRequestId: string | null;
  /** `scopes` is the narrowed subset the owner left checked. */
  onApprove: (request: DeviceEnrollmentRequestView, scopes: string[]) => void;
}

/** One pending request, as a decision a human can make in seconds: what
 * machine, what it wants, which code to match against the console in front of
 * them, and how long they have. */
function PendingDecision({
  request,
  busy,
  onApprove,
}: {
  request: DeviceEnrollmentRequestView;
  busy: boolean;
  onApprove: (request: DeviceEnrollmentRequestView, scopes: string[]) => void;
}) {
  const [granted, setGranted] = React.useState<string[]>(request.requestedScopes);
  const toggle = (scope: string, on: boolean) =>
    setGranted((current) =>
      on ? [...new Set([...current, scope])] : current.filter((s) => s !== scope),
    );
  return (
    <Card>
      <CardHeader>
        <CardTitle>{request.deviceLabel}</CardTitle>
        <CardDescription>
          Requested {formatRelativeTime(request.requestedAt)} · expires{" "}
          {formatDateTime(request.expiresAt)}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <span className="type-subtext text-muted-foreground">
            Match this code against the machine&rsquo;s console
          </span>
          <span className="font-mono type-title tracking-widest">{request.userCode}</span>
        </div>
        <div className="flex flex-col gap-2">
          <span className="type-subtext text-muted-foreground">
            Grant only what it needs — unchecking narrows the grant, and nothing
            can widen it
          </span>
          {request.requestedScopes.map((scope) => (
            <CheckboxField
              key={scope}
              label={<span className="font-mono">{scope}</span>}
              checked={granted.includes(scope)}
              onCheckedChange={(value) => toggle(scope, value === true)}
            />
          ))}
        </div>
        <div>
          <Button
            variant="accept"
            size="sm"
            disabled={busy || granted.length === 0}
            onClick={() => onApprove(request, granted)}
          >
            {busy ? "Approving…" : "Approve this device"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

const HISTORY_COLUMNS: DataTableColumn<DeviceEnrollmentRequestView>[] = [
  { key: "deviceLabel", header: "Device", render: (row) => row.deviceLabel },
  {
    key: "userCode",
    header: "Code",
    render: (row) => <span className="font-mono">{row.userCode}</span>,
  },
  {
    key: "state",
    header: "State",
    render: (row) => (
      <IntentBadge intent={STATE_INTENT[row.state]}>{STATE_LABEL[row.state]}</IntentBadge>
    ),
  },
  {
    key: "scopes",
    header: "Granted",
    render: (row) => (
      <span className="font-mono">
        {(row.approvedScopes ?? row.requestedScopes).join(", ")}
      </span>
    ),
  },
  {
    key: "requestedAt",
    header: "Requested",
    render: (row) => formatDateTime(row.requestedAt),
  },
];

export function DeviceApprovalsView({
  workspaceId,
  enrollments,
  isLoading,
  error,
  notice,
  busyRequestId,
  onApprove,
}: DeviceApprovalsViewProps) {
  const pending = enrollments.filter((row) => row.state === "pending");
  return (
    <SettingsPageShell
      title="Device approvals"
      description={
        workspaceId === null
          ? "Machines with no browser ask here for verification authority. You are the only thing that can grant it."
          : `Machines with no browser ask here for verification authority in workspace ${workspaceId}. You are the only thing that can grant it.`
      }
    >
      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Refused</AlertTitle>
          <AlertDescription>
            <p className="font-mono">{error}</p>
          </AlertDescription>
        </Alert>
      ) : null}
      {notice ? (
        <Alert>
          <AlertTitle>Approved</AlertTitle>
          <AlertDescription>
            <p>{notice}</p>
          </AlertDescription>
        </Alert>
      ) : null}

      <SettingsSection
        id="pending"
        title="Waiting on you"
        description="A pending request confers nothing. It becomes authority only when you say so, and only for the scopes you leave checked."
      >
        {isLoading && enrollments.length === 0 ? (
          <Card>
            <CardContent>
              <p className="type-body text-muted-foreground">Loading…</p>
            </CardContent>
          </Card>
        ) : pending.length === 0 ? (
          <Card>
            <CardContent>
              <p className="type-body text-muted-foreground">
                Nothing is waiting on you. A headless machine starts a request with{" "}
                <span className="font-mono">excellent-mcp device-enrollment begin</span> and
                prints a code to match here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="flex flex-col gap-4">
            {pending.map((request) => (
              <PendingDecision
                key={request.requestId}
                request={request}
                busy={busyRequestId === request.requestId}
                onApprove={onApprove}
              />
            ))}
          </div>
        )}
      </SettingsSection>

      <SettingsSection
        id="history"
        title="Every request"
        description="Consent is append-only: what was granted, to which machine, is a permanent record."
      >
        <DataTable
          rows={enrollments}
          columns={HISTORY_COLUMNS}
          rowKey={(row) => row.requestId}
          empty="No machine has ever asked this workspace for authority."
        />
      </SettingsSection>
    </SettingsPageShell>
  );
}
